'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2, MessageCircle, Send, X } from 'lucide-react';
import { useSiteChat } from './useSiteChat';

const SUGGESTIONS = [
  'How much does enrichment cost?',
  'How do I enrich a list?',
  'How do webhooks work?',
];

export default function SiteChatWidget() {
  const { open, setOpen, messages, pending, error, send } = useSiteChat();
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, pending]);

  const submit = (text?: string) => {
    const value = text ?? draft;
    void send(value);
    setDraft('');
  };

  return (
    <div className="fixed bottom-5 right-5 z-[70] flex flex-col items-end gap-3">
      {open && (
        <div
          className="w-[min(100vw-1.5rem,22.5rem)] h-[min(70vh,32rem)] terminal-border rounded-2xl flex flex-col overflow-hidden shadow-[0_16px_48px_rgba(0,0,0,0.5)]"
          role="dialog"
          aria-label="BrainScraper assistant"
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 bg-black/35">
            <div>
              <div className="text-sm font-semibold text-white">Assistant</div>
              <div className="text-[11px] text-gray-500">Answers from BrainScraper site info</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1.5 text-gray-400 hover:text-white hover:bg-white/10"
              aria-label="Close assistant"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`text-sm leading-relaxed rounded-xl px-3 py-2 max-w-[95%] ${
                  m.role === 'user'
                    ? 'ml-auto bg-[var(--accent-soft)] text-white'
                    : 'bg-white/[0.04] text-gray-200 border border-white/8'
                }`}
              >
                {m.content}
              </div>
            ))}
            {pending && (
              <div className="inline-flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Looking that up…
              </div>
            )}
            {error && <p className="text-xs text-red-300">{error}</p>}
            {messages.length < 3 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {SUGGESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => submit(q)}
                    className="text-[11px] rounded-full border border-white/12 px-2.5 py-1 text-gray-400 hover:text-white hover:border-white/25"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            className="border-t border-white/10 p-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="ui-input flex-1 px-3 py-2 text-sm"
              placeholder="Ask about BrainScraper…"
              maxLength={500}
              disabled={pending}
              aria-label="Message"
            />
            <button
              type="submit"
              disabled={pending || !draft.trim()}
              className="btn-primary px-3 py-2 disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-primary h-12 w-12 rounded-full inline-flex items-center justify-center shadow-[0_8px_28px_rgba(196,181,253,0.28)]"
        aria-label={open ? 'Close assistant' : 'Open assistant'}
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>
    </div>
  );
}
